import { Agreement } from '@varo/graphql-schema-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

interface CheckDepositImagesAndAgreementProps {
  checkImageUrls: string[];
  agreement?: Maybe<Agreement>;
  hyperlinkPressed: (agreement: Agreement | null) => void;
}

const CheckDepositImagesAndAgreement = ({
  checkImageUrls,
  agreement,
  hyperlinkPressed,
}: CheckDepositImagesAndAgreementProps) => {
  const { t: translatedText } = useTranslation('rdc');
  const [imageLoadStates, setImageLoadStates] = useState<boolean[]>(
    new Array(checkImageUrls.length).fill(false),
  );

  const handleImageLoadEnd = (index: number) => {
    const newLoadStates = [...imageLoadStates];
    newLoadStates[index] = true;
    setImageLoadStates(newLoadStates);
  };

  return (
    <View style={styles.container}>
      {checkImageUrls?.map((imageUrl, index) => (
        <View style={styles.imageContainer} key={imageUrl}>
          {!imageLoadStates[index] ? (
            <View
              style={styles.loader}
              accessibilityLabel="check-images-loader"
            >
              <LoadingSpinner />
            </View>
          ) : null}
          <Image
            source={{ uri: imageUrl }}
            style={styles.image}
            onLoadEnd={() => {
              if (!imageLoadStates[index]) {
                handleImageLoadEnd(index);
              }
            }}
            accessibilityLabel={`Check image ${index + 1}`}
            role="img"
            accessible
          />
        </View>
      ))}
      {agreement ? (
        <BodySmText style={styles.agreementContainer} numberOfLines={2}>
          {translatedText('checkDetails.agreementText')}
          <HyperlinkSmText onPress={() => hyperlinkPressed(agreement)}>
            {agreement.title}
          </HyperlinkSmText>
        </BodySmText>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  agreementContainer: {
    marginTop: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
  image: {
    height: '100%',
    resizeMode: 'contain',
    width: '100%',
  },
  imageContainer: {
    alignItems: 'center',
    borderColor: theme.color.interactive.black,
    borderWidth: 1,
    height: 100,
    justifyContent: 'center',
    marginBottom: theme.spacing.m,
    position: 'relative',
  },
  loader: {
    position: 'absolute',
  },
});

export default CheckDepositImagesAndAgreement;
